import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Wrench, Key, FileWarning, Plane, RefreshCw, Plus, Copy, Eye, 
  AlertTriangle, CheckCircle, XCircle, Activity, GitBranch, Clock,
  Link2, Database, Webhook
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

const SEVERITY_COLORS = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  major: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  minor: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  observation: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

export default function MROIntegrationPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState([]);
  const [findings, setFindings] = useState([]);
  const [correlations, setCorrelations] = useState(null);
  const [repeatDefects, setRepeatDefects] = useState(null);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [createdKey, setCreatedKey] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [keysRes, findingsRes, corrRes, repeatRes, logsRes] = await Promise.all([
        api.get('/mro/api-keys'),
        api.get('/mro/findings?days=30&limit=50'),
        api.get('/mro/correlation/defect-incidents?days=365'),
        api.get('/mro/correlation/repeat-defects?min_occurrences=2&days=180'),
        api.get('/mro/webhook-logs?days=7&limit=50'),
      ]);
      setApiKeys(keysRes.data || []);
      setFindings(findingsRes.data || []);
      setCorrelations(corrRes.data);
      setRepeatDefects(repeatRes.data);
      setWebhookLogs(logsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch MRO data:', error);
      toast.error('Failed to load MRO integration data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateApiKey = async () => {
    try {
      const response = await api.post(`/mro/api-keys?name=${encodeURIComponent(newKeyName)}&description=${encodeURIComponent(newKeyDescription)}`);
      setCreatedKey(response.data);
      toast.success('API key created');
      fetchData();
    } catch (error) {
      toast.error('Failed to create API key');
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key?')) return;
    try {
      await api.delete(`/mro/api-keys/${keyId}`);
      toast.success('API key revoked');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
            MRO Integration
          </h1>
          <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
            Connect any MRO system to import maintenance findings and track defect-incident correlations
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-sky-500/20">
                <Key className="w-5 h-5 text-sky-500" />
              </div>
              <div>
                <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                  {apiKeys.filter(k => k.is_active).length}
                </div>
                <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                  Active API Keys
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/20">
                <FileWarning className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                  {findings.length}
                </div>
                <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                  Findings (30 days)
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <GitBranch className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                  {correlations?.correlations_found || 0}
                </div>
                <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                  Defect-Incident Links
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Activity className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                  {repeatDefects?.repeat_defect_groups || 0}
                </div>
                <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                  Repeat Defect Groups
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="api-keys" className="w-full">
        <TabsList className={cn("grid w-full grid-cols-4", isDark ? "bg-slate-800" : "")}>
          <TabsTrigger value="api-keys">API Keys</TabsTrigger>
          <TabsTrigger value="findings">Findings</TabsTrigger>
          <TabsTrigger value="correlations">Correlations</TabsTrigger>
          <TabsTrigger value="logs">Webhook Logs</TabsTrigger>
        </TabsList>

        {/* API Keys Tab */}
        <TabsContent value="api-keys" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className={cn("text-lg font-semibold", isDark ? "text-white" : "")}>
                API Keys for MRO Systems
              </h2>
              <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                Generate API keys for your MRO system to push maintenance data
              </p>
            </div>
            <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
              <DialogTrigger asChild>
                <Button className="bg-sky-600 hover:bg-sky-700 gap-2">
                  <Plus className="w-4 h-4" />
                  Generate API Key
                </Button>
              </DialogTrigger>
              <DialogContent className={cn("max-w-md", isDark ? "bg-slate-900 border-slate-800" : "")}>
                <DialogHeader>
                  <DialogTitle className={isDark ? "text-white" : ""}>Generate New API Key</DialogTitle>
                </DialogHeader>
                
                {!createdKey ? (
                  <div className="space-y-4">
                    <div>
                      <Label className={isDark ? "text-slate-300" : ""}>Key Name *</Label>
                      <Input 
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        placeholder="e.g., AMOS Production"
                        className={isDark ? "bg-slate-950 border-slate-800" : ""}
                      />
                    </div>
                    <div>
                      <Label className={isDark ? "text-slate-300" : ""}>Description</Label>
                      <Input 
                        value={newKeyDescription}
                        onChange={(e) => setNewKeyDescription(e.target.value)}
                        placeholder="e.g., AMOS MRO system integration"
                        className={isDark ? "bg-slate-950 border-slate-800" : ""}
                      />
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowKeyDialog(false)}>Cancel</Button>
                      <Button onClick={handleCreateApiKey} className="bg-sky-600 hover:bg-sky-700">
                        Generate Key
                      </Button>
                    </DialogFooter>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className={cn("font-medium", isDark ? "text-white" : "")}>API Key Created</span>
                      </div>
                      <p className={cn("text-sm mb-3", isDark ? "text-slate-300" : "text-gray-600")}>
                        Store this key securely. It will not be shown again.
                      </p>
                      <div className="flex items-center gap-2">
                        <code className={cn(
                          "flex-1 p-2 rounded text-xs font-mono overflow-x-auto",
                          isDark ? "bg-slate-950 text-green-400" : "bg-gray-100"
                        )}>
                          {createdKey.api_key}
                        </code>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(createdKey.api_key)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={() => {
                        setCreatedKey(null);
                        setNewKeyName('');
                        setNewKeyDescription('');
                        setShowKeyDialog(false);
                      }}>
                        Done
                      </Button>
                    </DialogFooter>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>

          {/* Integration Guide */}
          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-blue-50 border-blue-200")}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Webhook className={cn("w-5 h-5 mt-0.5", isDark ? "text-sky-400" : "text-blue-600")} />
                <div>
                  <h3 className={cn("font-medium", isDark ? "text-white" : "text-blue-900")}>
                    Integration Guide
                  </h3>
                  <p className={cn("text-sm mt-1", isDark ? "text-slate-300" : "text-blue-700")}>
                    Use the webhook endpoint to push maintenance findings from your MRO system:
                  </p>
                  <code className={cn(
                    "block mt-2 p-2 rounded text-xs font-mono",
                    isDark ? "bg-slate-950 text-sky-400" : "bg-white text-blue-800"
                  )}>
                    POST /api/mro/webhook
                    <br />
                    Header: X-API-Key: your_api_key
                  </code>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Keys Table */}
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-0">
              {apiKeys.length === 0 ? (
                <div className="p-8 text-center">
                  <Key className="w-12 h-12 mx-auto mb-3 opacity-50 text-slate-400" />
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                    No API keys generated yet
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className={isDark ? "border-slate-800" : ""}>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Name</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Key Prefix</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Created</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Last Used</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Status</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {apiKeys.map(key => (
                      <TableRow key={key.id} className={isDark ? "border-slate-800" : ""}>
                        <TableCell>
                          <div className={cn("font-medium", isDark ? "text-white" : "")}>
                            {key.name}
                          </div>
                          {key.description && (
                            <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                              {key.description}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <code className={cn("text-xs font-mono", isDark ? "text-sky-400" : "")}>
                            {key.key_prefix}...
                          </code>
                        </TableCell>
                        <TableCell className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                          {key.created_at ? format(new Date(key.created_at), 'MMM d, yyyy') : '-'}
                        </TableCell>
                        <TableCell className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                          {key.last_used ? format(new Date(key.last_used), 'MMM d, HH:mm') : 'Never'}
                        </TableCell>
                        <TableCell>
                          <Badge className={key.is_active 
                            ? "bg-green-500/20 text-green-400" 
                            : "bg-red-500/20 text-red-400"
                          }>
                            {key.is_active ? 'Active' : 'Revoked'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {key.is_active && (
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleRevokeKey(key.id)}
                            >
                              Revoke
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Findings Tab */}
        <TabsContent value="findings" className="space-y-4">
          <h2 className={cn("text-lg font-semibold", isDark ? "text-white" : "")}>
            Imported Maintenance Findings
          </h2>
          
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-0">
              {findings.length === 0 ? (
                <div className="p-8 text-center">
                  <FileWarning className="w-12 h-12 mx-auto mb-3 opacity-50 text-slate-400" />
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                    No maintenance findings imported yet
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className={isDark ? "border-slate-800" : ""}>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Finding</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Aircraft</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Severity</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Category</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Status</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Found Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {findings.map(f => (
                      <TableRow key={f.id} className={isDark ? "border-slate-800" : ""}>
                        <TableCell>
                          <div className={cn("font-medium", isDark ? "text-white" : "")}>
                            {f.title}
                          </div>
                          <div className={cn("text-xs font-mono", isDark ? "text-slate-400" : "text-gray-500")}>
                            {f.external_id}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Plane className="w-4 h-4 text-sky-500" />
                            <span className={cn("font-mono", isDark ? "text-white" : "")}>
                              {f.aircraft_registration}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("capitalize border", SEVERITY_COLORS[f.severity])}>
                            {f.severity}
                          </Badge>
                        </TableCell>
                        <TableCell className={cn("capitalize text-sm", isDark ? "text-slate-300" : "")}>
                          {f.category?.replace('_', ' ')}
                        </TableCell>
                        <TableCell>
                          <Badge className={f.status === 'open' 
                            ? "bg-yellow-500/20 text-yellow-400" 
                            : "bg-green-500/20 text-green-400"
                          }>
                            {f.status}
                          </Badge>
                        </TableCell>
                        <TableCell className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                          {f.found_date}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Correlations Tab */}
        <TabsContent value="correlations" className="space-y-4">
          <h2 className={cn("text-lg font-semibold", isDark ? "text-white" : "")}>
            Defect-Incident Correlations
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Correlation Summary */}
            <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
              <CardHeader>
                <CardTitle className={cn("text-base", isDark ? "text-white" : "")}>
                  <GitBranch className="w-5 h-5 inline mr-2 text-red-500" />
                  Defect → Incident Links
                </CardTitle>
                <CardDescription>
                  Findings that preceded safety incidents within 30 days
                </CardDescription>
              </CardHeader>
              <CardContent>
                {correlations && correlations.correlations?.length > 0 ? (
                  <div className="space-y-3">
                    {correlations.correlations.slice(0, 5).map((corr, idx) => (
                      <div key={idx} className={cn(
                        "p-3 rounded-lg",
                        isDark ? "bg-slate-800" : "bg-gray-50"
                      )}>
                        <div className={cn("font-medium", isDark ? "text-white" : "")}>
                          {corr.incident_title}
                        </div>
                        <div className={cn("text-xs mt-1", isDark ? "text-slate-400" : "text-gray-500")}>
                          <Plane className="w-3 h-3 inline mr-1" />
                          {corr.aircraft_registration} • {corr.related_findings_count} related findings
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                    <p className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                      No correlations found
                    </p>
                    <p className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                      This is good - no defects preceded incidents
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Repeat Defects */}
            <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
              <CardHeader>
                <CardTitle className={cn("text-base", isDark ? "text-white" : "")}>
                  <Activity className="w-5 h-5 inline mr-2 text-purple-500" />
                  Repeat Defects
                </CardTitle>
                <CardDescription>
                  Components or systems with recurring maintenance findings
                </CardDescription>
              </CardHeader>
              <CardContent>
                {repeatDefects && repeatDefects.defects?.length > 0 ? (
                  <div className="space-y-3">
                    {repeatDefects.defects.slice(0, 5).map((defect, idx) => (
                      <div key={idx} className={cn(
                        "p-3 rounded-lg",
                        isDark ? "bg-slate-800" : "bg-gray-50"
                      )}>
                        <div className="flex justify-between items-start">
                          <div>
                            <div className={cn("font-medium", isDark ? "text-white" : "")}>
                              ATA {defect.ata_chapter}: {defect.part_number}
                            </div>
                            <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                              {defect.aircraft_count} aircraft affected
                            </div>
                          </div>
                          <Badge className={cn(
                            "border",
                            defect.most_severe === 'critical' ? SEVERITY_COLORS.critical :
                            defect.most_severe === 'major' ? SEVERITY_COLORS.major :
                            SEVERITY_COLORS.minor
                          )}>
                            {defect.total_occurrences}x
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                    <p className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                      No repeat defects detected
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Webhook Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          <h2 className={cn("text-lg font-semibold", isDark ? "text-white" : "")}>
            Webhook Processing Logs
          </h2>
          
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-0">
              {webhookLogs.length === 0 ? (
                <div className="p-8 text-center">
                  <Database className="w-12 h-12 mx-auto mb-3 opacity-50 text-slate-400" />
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                    No webhook events received yet
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className={isDark ? "border-slate-800" : ""}>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Event Type</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Source</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Status</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Received</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {webhookLogs.map(log => (
                      <TableRow key={log.id} className={isDark ? "border-slate-800" : ""}>
                        <TableCell className={cn("font-mono text-sm", isDark ? "text-white" : "")}>
                          {log.event_type}
                        </TableCell>
                        <TableCell className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                          {log.source_system}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            log.status === 'processed' ? "bg-green-500/20 text-green-400" :
                            log.status === 'error' ? "bg-red-500/20 text-red-400" :
                            "bg-yellow-500/20 text-yellow-400"
                          }>
                            {log.status}
                          </Badge>
                        </TableCell>
                        <TableCell className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                          {log.received_at ? format(new Date(log.received_at), 'MMM d, HH:mm:ss') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
